import { EyeInvisibleOutlined, EyeOutlined } from '@ant-design/icons'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCallback, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import AtomButton from '@/components/Atoms/AtomButton/AtomButton'
import { usePasswordStrength } from '@/hooks/auth/usePasswordStrength'
import { ResetPasswordFormData, ResetPasswordFullSchema } from '@/schemas'
import { useCartStore } from '@/stores/cart/cartStore'
import { useFeedStore } from '@/stores/feed/feedStore'
import { useSearchStore } from '@/stores/feed/searchStore'
import { publicApi, setMemoryToken } from '@/utils/api/api'
import getGeneralErrorMessage from '@/utils/api/errors/getGeneralErrorMessage'
import { ensureWebAccess, WebAccessDeniedError } from '@/utils/auth/webAccess'
import handleFormSubmitError from '@/utils/forms/handleFormSubmitError'
import { denormalizePhoneForBackend } from '@/utils/phone/denormalizePhoneForBackend'

import cn from '../Forms.module.scss'
import { PhoneInput } from '../PhoneInput/PhoneInput'
import { SMSCodeInput } from '../SMSCodeInput/SMSCodeInput'

type Step = 'phone' | 'code' | 'password'

type ResetFormValues = ResetPasswordFormData

export default function ResetPasswordForm({
  initialPhone,
}: {
  initialPhone?: string
}) {
  const [step, setStep] = useState<Step>('phone')
  const [formattedPhone, setFormattedPhone] = useState('')
  const [vCode, setVCode] = useState('')
  const [isSuccessAnimating, setIsSuccessAnimating] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const navigate = useNavigate()

  const { result: strengthResult, checkStrength } = usePasswordStrength()

  const {
    control,
    handleSubmit,
    trigger,
    getValues,
    setError,
    formState: { errors },
  } = useForm<ResetFormValues>({
    resolver: zodResolver(ResetPasswordFullSchema),
    defaultValues: {
      phone: initialPhone || '',
      password: '',
      repeatPassword: '',
    },
  })

  const handleRequestCode = async () => {
    const isPhoneValid = await trigger('phone')
    if (!isPhoneValid) return

    const phoneValue = getValues('phone')
    const digitsOnly = denormalizePhoneForBackend(phoneValue)
    setFormattedPhone(digitsOnly)

    toast.promise(publicApi.post(`/sms/send`, { phoneNumber: digitsOnly }), {
      loading: 'Отправка кода...',
      success: () => {
        setStep('code')
        return 'Код подтверждения отправлен'
      },
      error: (error) =>
        error.response?.data?.message || getGeneralErrorMessage(error),
    })
  }

  const handleVerifyCode = useCallback(
    async (code: string) => {
      setIsSuccessAnimating(true)

      publicApi
        .post(
          `/sms/verify`,
          {
            phoneNumber: formattedPhone,
            verificationCode: code,
          },
          { _skipAuth: true }
        )
        .then(() => {
          setVCode(code)
          setStep('password')
          setIsSuccessAnimating(false)
        })
        .catch((error) => {
          setIsSuccessAnimating(false)
          const serverMessage =
            error.response?.data?.message || getGeneralErrorMessage(error)
          trigger('phone').then(() => {
            setError('phone', { type: 'manual', message: serverMessage })
          })
          toast.error(serverMessage)
        })
    },
    [formattedPhone, setError]
  )

  const onSubmitNewPassword = async (data: ResetFormValues) => {
    const loadingToast = toast.loading('Обновление пароля...')

    publicApi
      .post(
        '/sms/reset-password',
        {
          phoneNumber: formattedPhone,
          verificationCode: vCode,
          newPassword: data.password,
        },
        { _skipAuth: true }
      )
      .then(ensureWebAccess)
      .then((response) => {
        localStorage.removeItem('currentClientId')
        localStorage.removeItem('selectedOrganisationCode')
        localStorage.removeItem('cart-storage')
        useSearchStore.getState().setInitialSearchStore()
        useFeedStore.getState().setInitialFeedStore()
        useCartStore.getState().setInitialCartStore()

        const { accessToken, refreshToken } = response.data.token
        setMemoryToken(accessToken)
        localStorage.setItem('refresh-token', refreshToken)

        toast.success('Пароль успешно изменен!', {
          id: loadingToast,
        })

        navigate('/', { replace: true })
      })
      .catch((error) => {
        toast.dismiss(loadingToast)
        const serverMessage =
          error instanceof WebAccessDeniedError
            ? error.message
            : error.response?.data?.message || getGeneralErrorMessage(error)
        toast.error(serverMessage)
      })
  }

  return (
    <div className={cn.formBlock}>
      {step === 'phone' && (
        <div className={cn.resetStep}>
          <div className={cn.inputs}>
            <div className={cn.inputWrapper}>
              <label className={cn.label} htmlFor="reset-phone">
                Телефон
              </label>
              <PhoneInput
                id="reset-phone"
                name="phone"
                control={control}
                errors={errors}
              />
              {errors.phone && (
                <p className={cn.errorMessage}>{errors.phone.message}</p>
              )}
            </div>
          </div>
          <AtomButton fullWidth onClick={handleRequestCode}>
            Отправить СМС с кодом
          </AtomButton>
        </div>
      )}

      {step === 'code' && (
        <div className={cn.resetStep}>
          <div className={cn.inputs}>
            <div className={cn.phoneReminder}>
              <div className={cn.inputWrapper}>
                <label className={cn.label} htmlFor="reset-code-phone">
                  Телефон
                </label>
                <PhoneInput
                  id="reset-code-phone"
                  name="phone"
                  control={control}
                  errors={errors}
                />
                {errors.phone && (
                  <p className={cn.errorMessage}>{errors.phone.message}</p>
                )}
              </div>
            </div>
          </div>

          <SMSCodeInput
            onComplete={handleVerifyCode}
            isSuccess={isSuccessAnimating}
          />
          <div className={cn.codeHint}>Введите код из СМС</div>
        </div>
      )}

      {step === 'password' && (
        <form
          onSubmit={handleSubmit(
            onSubmitNewPassword,
            handleFormSubmitError(false)
          )}
        >
          <div className={cn.inputs}>
            <div className={cn.inputWrapper}>
              <label className={cn.label} htmlFor="reset-password">
                Новый пароль
              </label>
              <div className={cn.inputWithEye}>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <input
                      {...field}
                      id="reset-password"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Новый пароль"
                      className={[
                        cn.input,
                        errors.password ? cn.inputInvalid : cn.inputValid,
                      ].join(' ')}
                      onChange={(e) => {
                        const value = e.target.value.replace(
                          /[\u0400-\u04FF]/g,
                          ''
                        )
                        field.onChange(value)
                        checkStrength(value)
                      }}
                    />
                  )}
                />

                <button
                  type="button"
                  className={cn.eyeOutlinedContainer}
                  aria-label={
                    showPassword ? 'Скрыть пароль' : 'Показать пароль'
                  }
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                </button>
              </div>
              {errors.password && (
                <p className={cn.errorMessage}>{errors.password.message}</p>
              )}
              <div
                className={`${cn.strengthContainer} ${strengthResult ? cn.active : ''}`}
              >
                <div className={cn.strengthMeter}>
                  <div className={cn.strengthBar}>
                    {strengthResult ? (
                      <div
                        className={cn.strengthFill}
                        style={{ '--strength': `${strengthResult.strength}%` }}
                      />
                    ) : (
                      <div className={cn.skeletonBarFill} />
                    )}
                  </div>
                  <span className={cn.strengthText}>
                    {strengthResult?.label ?? 'Введите пароль'} (
                    {strengthResult?.strength ?? 0}%)
                  </span>
                </div>

                {/* <div className={cn.strengthCriteria}>
                  <div className={cn.criteriaRow}>
                    <div className={cn.criteriaItem}>
                      <span
                        className={`${cn.checkmark} ${strengthResult?.criteria.lengthOk ? cn.valid : ''}`}
                      >
                        ✓
                      </span>
                      <span>8+ символов</span>
                    </div>
                    <div className={cn.criteriaItem}>
                      <span
                        className={`${cn.checkmark} ${strengthResult?.criteria.hasUpper ? cn.valid : ''}`}
                      >
                        ✓
                      </span>
                      <span>Заглавная</span>
                    </div>
                  </div>
                  <div className={cn.criteriaRow}>
                    <div className={cn.criteriaItem}>
                      <span
                        className={`${cn.checkmark} ${strengthResult?.criteria.hasNumber ? cn.valid : ''}`}
                      >
                        ✓
                      </span>
                      <span>Цифры</span>
                    </div>
                    <div className={cn.criteriaItem}>
                      <span
                        className={`${cn.checkmark} ${strengthResult?.criteria.hasSpecial ? cn.valid : ''}`}
                      >
                        ✓
                      </span>
                      <span>Символы</span>
                    </div>
                  </div>
                </div> */}
              </div>
            </div>

            <div className={cn.inputWrapper}>
              <label className={cn.label} htmlFor="reset-repeat-password">
                Повторите пароль
              </label>
              <Controller
                name="repeatPassword"
                control={control}
                render={({ field }) => (
                  <input
                    {...field}
                    id="reset-repeat-password"
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Подтвердите пароль"
                    className={[
                      cn.input,
                      errors.repeatPassword ? cn.inputInvalid : cn.inputValid,
                    ].join(' ')}
                    onChange={(e) => {
                      const value = e.target.value.replace(
                        /[\u0400-\u04FF]/g,
                        ''
                      )
                      field.onChange(value)
                    }}
                  />
                )}
              />
              {errors.repeatPassword && (
                <p className={cn.errorMessage}>
                  {errors.repeatPassword.message}
                </p>
              )}
            </div>
          </div>
          <AtomButton fullWidth htmlType="submit">
            Установить пароль
          </AtomButton>

          <div className={cn.passwordHint}>
            <p className={cn.smallText}>
              💡<strong>Важно!</strong> Сохраните новый пароль в менеджере
              паролей или надежном месте прямо сейчас.
            </p>
          </div>
        </form>
      )}
    </div>
  )
}
